/**
 * O valor do perito numa linha de leitura (E211.F4, owner, 01/11; ADR-0146, adendo E211.F4): a
 * natureza do endereço de rede (seção 7) e a localização e a distância da referência da
 * geolocalização (seção 6). O perito escreve o valor dele, a fonte (opcional) e o fundamento; a
 * tabela imprime o valor com a nota numerada, que traz o fundamento e a fonte, e o valor lido fica
 * no histórico do caso (o anterior da revisão).
 *
 * Cada coluna mudada grava a própria revisão (`<linha>:<coluna>`), com a mesma fonte e o mesmo
 * fundamento; o Salvar fica ligado e diz o que falta.
 */
import { useState } from 'react';

import { Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { colunasDoValor, type ColunaDoValor } from '@/lib/revisaoDoDado';

import { Fundamento, faltaNoFundamento } from './Fundamento';
import { BotaoSalvar, Falta, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.valor;
const falta = ptBR.painelDoDado.falta;

export function ValorNoPainel(props: PropsDoConteudo) {
  const { pedido, acoes, onMudou } = props;
  const linha = pedido.origem.linha ?? '';
  const [colunas] = useState(() =>
    colunasDoValor(pedido.origem.linha, pedido.origem.valor, pedido.origem.texto),
  );
  const [valores, setValores] = useState<Partial<Record<ColunaDoValor, string>>>(() =>
    Object.fromEntries(colunas.map((c) => [c.coluna, c.impresso])),
  );
  const [fonte, setFonte] = useState('');
  const [fundamento, setFundamento] = useState('');
  const [oQueFalta, setOQueFalta] = useState<string | null>(null);
  const mudadas = colunas.filter((c) => {
    const novo = (valores[c.coluna] ?? '').trim();
    return novo !== '' && novo !== c.impresso.trim();
  });
  const gravacao = useGravacao(
    async () => {
      for (const { coluna } of mudadas) {
        const recusa = await gravarRevisao(props, {
          alvo: 'linha',
          chave: `${linha}:${coluna}`,
          valor: (valores[coluna] ?? '').trim(),
          texto: fonte.trim() === '' ? null : fonte.trim(),
          fundamento: fundamento.trim(),
        });
        if (recusa !== null) return recusa;
      }
      return null;
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  function salvar(): void {
    const pendente = mudadas.length === 0 ? falta.valorLivre : faltaNoFundamento(fundamento);
    setOQueFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }
  return (
    <div className="space-y-sc-3 border-t border-border pt-sc-3" data-testid="painel-valor">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      {colunas.map(({ coluna }) => (
        <Field key={coluna} label={t.colunas[coluna]} htmlFor={`painel-valor-${coluna}`}>
          <Input
            id={`painel-valor-${coluna}`}
            value={valores[coluna] ?? ''}
            onChange={(e) => setValores((atual) => ({ ...atual, [coluna]: e.target.value }))}
            data-testid={`painel-valor-${coluna}`}
          />
        </Field>
      ))}
      <Field label={t.fonte} description={t.dicaDaFonte} htmlFor="painel-valor-fonte">
        <Input
          id="painel-valor-fonte"
          value={fonte}
          onChange={(e) => setFonte(e.target.value)}
          data-testid="painel-valor-fonte"
        />
      </Field>
      <Fundamento id="painel-valor-fundamento" valor={fundamento} onChange={setFundamento} />
      <Falta falta={oQueFalta} estado={gravacao.estado} testId="painel-valor-falta" />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-valor-salvar"
      />
    </div>
  );
}
